import React, { Fragment, useMemo, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import { eventTransform } from '@components/Brella/transform';
import useScrollToHash from '@hooks/useScrollToHash';

import EventNavigation from './EventNavigation';
import EventRegistration from './EventRegistration';
import { EventShowPageContentProps, TIME_FORMAT } from './interfaces';
import './styles.scss';

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC midnight shifting the displayed day). */
function parseIsoDateLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const EventShowPageContent = ({
  event,
  eventOffer,
  upgradeOffer,
  currentUser,
}: EventShowPageContentProps) => {
  const [isAgendaOpen, setIsAgendaOpen] = useState(true);
  const pictureUrl = event.pictureUrl;
  const speakersRef = useScrollToHash({ hash: '#speakers', offset: 100 });
  const agendaRef = useScrollToHash({ hash: '#agenda', offset: 100 });
  const partnersRef = useScrollToHash({ hash: '#partners', offset: 100 });

  const toggleAgenda = () => {
    setIsAgendaOpen(!isAgendaOpen);
  };

  const eventTimeRange = useMemo(() => {
    const timeZone = ` ${event.timeZone}` || '';
    if (!event.endTime) {
      return `${event.startTime}${timeZone}`;
    }
    return `${event.startTime}${timeZone} - ${event.endTime}${timeZone}`;
  }, [event.startTime, event.endTime, event.timeZone]);

  // Render additional day times if present
  const renderAdditionalDayTimes = ({ dateOnly = false }: { dateOnly?: boolean }) => {
    if (!event.additionalDayTimes) return null;

    const lines = event.additionalDayTimes.split('\n').filter((line) => line.trim());

    if (dateOnly) {
      return event.date;
    }

    return lines.map((timeStr, idx) => {
      if (!timeStr.trim()) return null;

      const date = parseIsoDateLocal(event.startDateIso);
      date.setDate(date.getDate() + idx + 1);

      return (
        <Fragment key={idx}>
          <br />
          <br />
          {date.toLocaleDateString('en-US', TIME_FORMAT)}
          <br />
          {timeStr}
        </Fragment>
      );
    });
  };

  return (
    <div className="container event-container">
      <div className="row">
        <div className="col-xs-12">
          <div className="event-show-img">
            {pictureUrl ? (
              <picture>
                <source
                  type="image/webp"
                  media="(min-width: 1px) and (max-width: 991px)"
                  width="730"
                  height="411"
                  srcSet={`${pictureUrl}?w=730&h=411&format=webp`}
                />
                <source
                  type="image/webp"
                  media="(min-width: 992px) and (max-width: 1349px)"
                  width="920"
                  height="518"
                  srcSet={`${pictureUrl}?w=920&h=518&format=webp`}
                />
                <source
                  type="image/webp"
                  media="(min-width: 1350px)"
                  width="1030"
                  height="580"
                  srcSet={`${pictureUrl}?w=1030&h=580&format=webp`}
                />
                <source
                  type="image/jpeg"
                  width="1030"
                  height="580"
                  srcSet={`${pictureUrl}?w=1030&h=580&format=jpeg`}
                />
                <img
                  src={`${pictureUrl}?w=1030&h=580&format=jpeg`}
                  alt="Event Image"
                  width="1030"
                  height="580"
                />
              </picture>
            ) : (
              <img
                src="https://dmxvlyap9srmn.cloudfront.net/production/events/events_meta.png"
                className="main-event-image"
                alt="Event Image"
              />
            )}
          </div>
        </div>
      </div>

      <div className="row mt-24 md:mt-64">
        <div className="col-md-8 event-name-column">
          {event.highlight && <div className="highlight mr1 !inline-block">{event.highlight}</div>}

          <h1 className="event-title">{event.name}</h1>

          <div className="detail-text">{renderAdditionalDayTimes({ dateOnly: true })}</div>

          {event.description && (
            <div className="description">
              {ReactHtmlParser(event.description, { transform: eventTransform })}
            </div>
          )}

          {event.furtherDetails && !event.registrationHasPassed && (
            <div className="more-details">
              <div className="more-details-content">
                {ReactHtmlParser(event.furtherDetails, { transform: eventTransform })}
              </div>
            </div>
          )}
        </div>

        <div className="col-md-4 event-name-column">
          <EventNavigation
            hasSpeakers={!!event.eventSpeakers}
            hasAgenda={!!event.eventAgenda}
            hasPartners={!!event.coHostsAndVips}
          />
          <EventRegistration
            event={event}
            eventOffer={eventOffer}
            upgradeOffer={upgradeOffer}
            currentUser={currentUser}
          />

          <div className="show-event-date">
            <div className="highlight-text event-highlight">When</div>
            <div className="detail-text show-event-details">
              {event.date}
              <br />
              {eventTimeRange}
              {renderAdditionalDayTimes({})}
            </div>

            <div className="highlight-text event-highlight">Where</div>
            <div className="detail-text show-event-details">
              {event.venue && (
                <>
                  {event.venue}
                  <br />
                </>
              )}
              {event.address && <>{event.address}, </>}
              {event.location && <>{event.location}</>}
            </div>

            {event.whoCanAttend && (
              <>
                <div className="highlight-text event-highlight">Who can attend</div>
                <div className="detail-text show-event-details">
                  {ReactHtmlParser(event.whoCanAttend, { transform: eventTransform })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {event.eventSpeakers && (
        <section className="row" ref={speakersRef} id="speakers">
          <div className="agenda-header">Speakers</div>
          <div className="event-speakers">
            {ReactHtmlParser(event.eventSpeakers, { transform: eventTransform })}
          </div>
        </section>
      )}

      {event.eventAgenda && (
        <section className="row" ref={agendaRef} id="agenda">
          <div className="agenda-header" onClick={toggleAgenda}>
            Agenda <i className={`fa fa-angle-${isAgendaOpen ? 'down' : 'right'}`} />
          </div>
          <div
            className={`agenda-details event-agenda padding-adjust ${
              !isAgendaOpen ? 'hidden' : ''
            }`}
            style={{ display: isAgendaOpen ? 'block' : 'none' }}
          >
            {ReactHtmlParser(event.eventAgenda, { transform: eventTransform })}
          </div>
        </section>
      )}

      {event.coHostsAndVips && (
        <section className="row" ref={partnersRef} id="partners">
          <div className="event-vips">
            {ReactHtmlParser(event.coHostsAndVips, { transform: eventTransform })}
          </div>
        </section>
      )}
    </div>
  );
};

export default EventShowPageContent;
